import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWatch } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { cssVar, Button, IconButton, Spinner, Text, DataList, DataItem, Menu, MenuTrigger, MenuContent, MenuItem } from '@cad-arc';
import { EventPanelMetadata } from '@mark43/cad-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { FrPageHeader } from '../fr-page-layout/fr-page-header';
import { useEventMetadataStore } from '../event-panel/event-panel-form/event-metadata';
import { EntityStatusIcon } from '../entity-status-icon';
import { useCopyText } from '../common/hooks/use-copy-text';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import {
    FR_EDIT_DISPOSITION_SUB_PATH,
    FR_EDIT_EVENT_SUB_PATH,
    FR_EVENT_ROOT_PATH,
} from '../router/constants';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { useIntegratedForm } from '../forms/use-integrated-form';
import { StreamMap } from '@m43/core';
import { MessagePayload } from '@m43/sockets-mark43';
import { useDispositionQuery } from '../dispositions/hooks/use-disposition-query';
import { useIntegratedStreams } from '../common/hooks/use-integrated-streams';
import { matchesDispositionNudge } from '../event-panel/event-state/matches-disposition-nudge';
import { FrEventUnitPills } from './fr-event-unit-pills';
import { FrEventFormType } from '../fr-entity-panels/types';
import { EventLabelsView } from '../event-labels-view';
import { HighlightBox } from '../common/components/highlighted-box';
import { useEventLocationNotifications } from '../common/hooks/use-event-location-notifications';

export const FrEventPageHeader = observer(function FrEventPageHeader({
    isClearEventPage = false,
    isUnassignCurrentUserUnitPage = false,
    isEditDispositionPage = false,
}: {
    isClearEventPage?: boolean;
    isUnassignCurrentUserUnitPage?: boolean;
    isEditDispositionPage?: boolean;
} = {}) {
    const eventMetadataStore = useEventMetadataStore();
    const {
        agencyEventNumber,
        isActive,
        isScheduledEvent,
        eventId,
        callForServiceCode,
        callForServiceDescription,
        pillUnits,
    } = eventMetadataStore?.state?.metadata ?? ({} as EventPanelMetadata);

    const unitAssignment = useUnitAssignment();
    const {
        eventLabels: eventLabelsEnabled,
        editClearedEvents: editClearedEventsEnabled,
        unitLevelDispositionsEnabled,
    } = useEnabledFeatures();
    const navigate = useNavigate();
    const { form } = useIntegratedForm<FrEventFormType>();
    const copyText = useCopyText();
    const eventLocation = useWatch({ control: form.control, name: 'location' });

    const isEventLoading = !callForServiceCode || !callForServiceDescription;

    const isAssignedEvent = eventId ? eventId === unitAssignment?.assignedEvent?.eventId : false;
    const isClearedEvent = !!eventId && !isActive && !isScheduledEvent;

    /* An active event can carry a disposition ONLY when unit-level dispositions are on (e.g. the
     * primary unit was released with one) — mirror CAD and let that be edited too. With ULD off a
     * disposition lives on the event-clearing record, so an active event has no editable disposition
     * (the backend rejects editing an active event's disposition when ULD is off); gate on ULD so we
     * don't offer Edit Disposition for a stale clearing disposition on a since-reactivated event.
     * Only fetch when active + assigned; a cleared event already offers the dropdown. A hollow release
     * row (no disposition attribute) resolves to a null primary disposition, so it does not count. */
    const shouldCheckActiveDisposition =
        !!eventId &&
        isActive &&
        isAssignedEvent &&
        editClearedEventsEnabled &&
        unitLevelDispositionsEnabled;
    const dispositionQuery = useDispositionQuery({
        agencyEventId: eventId,
        enabled: shouldCheckActiveDisposition,
    });
    const dispositionData = dispositionQuery.data;

    /* Refetch the disposition on DISP/EC nudges so the Edit Disposition option appears live when a
     * disposition lands on the active event while this user stays assigned and viewing it. This
     * happens on a multi-unit event where another (non-assigned) unit is released with a disposition:
     * the event stays active, the change isn't driven by this user, so without a nudge refetch the
     * option would only surface on a remount. Mirrors the CAD kebab menu. */
    const onDispositionNudge = useCallback(
        (message: MessagePayload<StreamMap, 'nudge'>): number[] =>
            matchesDispositionNudge(message, eventId),
        [eventId]
    );
    useIntegratedStreams({
        query: dispositionQuery,
        streamNames: ['nudge'],
        enabled: shouldCheckActiveDisposition,
        promiseBufferCallback: () => dispositionQuery.refetch(),
        onMessage: onDispositionNudge,
    });

    /* "Has a disposition" means a disposition ROW exists, not that a disposition value was chosen —
     * matching CAD, which keys off row existence. A unit released without picking a disposition still
     * creates a primary row (null attr) so the user can add one later via Edit Disposition; that row
     * carries the owning unit (`primaryUnitIdFromDisposition`) even though the attribute is null, so
     * check it too — `primaryDisposition` alone (the resolved attribute) would miss the hollow row. */
    const activeEventHasDisposition =
        dispositionData?.form?.primaryDisposition != null ||
        (dispositionData?.form?.additionalDispositions?.length ?? 0) > 0 ||
        dispositionData?.form?.primaryUnitIdFromDisposition != null;

    /* A cleared event the user may edit — or an active event that already has a disposition — offers
     * the Edit dropdown (Edit Event Details / Edit Disposition), regardless of unit-level
     * dispositions. With ULD off the disposition is edited on the event-clearing record instead of
     * per-unit, but the FR flow is identical. The single Edit button remains only for active
     * assigned events with no disposition to edit. */
    const canEditClearedEvent = isClearedEvent && editClearedEventsEnabled;
    const showEditDispositionMenu =
        canEditClearedEvent || (shouldCheckActiveDisposition && activeEventHasDisposition);
    const showSingleEditButton = !showEditDispositionMenu && isAssignedEvent;

    const [firstRen] = eventMetadataStore?.eventRens ?? [];
    const renLabel = useBootstrapState()?.fieldDisplayNameByFieldName?.[
        'AGENCY_EVENT_REPORTING_EVENT_NUMBER'
    ];

    const pageTitleContent = `${callForServiceCode} - ${callForServiceDescription}`;
    const pageTitlePrefix = isClearEventPage
        ? 'Clear '
        : isUnassignCurrentUserUnitPage
        ? 'Unassign Myself From '
        : isEditDispositionPage
        ? 'Edit Disposition for '
        : '';
    const pageTitle = `${pageTitlePrefix}${pageTitleContent}`;

    const shouldShowHeaderActions =
        !isClearEventPage && !isUnassignCurrentUserUnitPage && !isEditDispositionPage;

    const goToEditEventPage = useCallback(() => {
        navigate(`${FR_EVENT_ROOT_PATH}/${eventId}/${FR_EDIT_EVENT_SUB_PATH}`);
    }, [navigate, eventId]);

    const goToEditDispositionPage = useCallback(() => {
        navigate(`${FR_EVENT_ROOT_PATH}/${eventId}/${FR_EDIT_DISPOSITION_SUB_PATH}`);
    }, [navigate, eventId]);

    const assignedEventId = unitAssignment?.assignedEvent?.eventId;
    const {
        isLocationUpdated,
        removeEventLocationNotifications,
    } = useEventLocationNotifications(assignedEventId, { autoClear: true });

    return (
        <FrPageHeader
            title={isEventLoading ? null : pageTitle}
            showGoBackButton
            complementaryContent={
                eventId && (
                    <DataList
                        direction="row"
                        spacing={4}
                        style={{ fontSize: cssVar('arc.fontSizes.md'), alignItems: 'center' }}
                    >
                        {eventLocation?.displayLineFirst && (
                            <HighlightBox
                                highlight={isLocationUpdated}
                                onClick={removeEventLocationNotifications}
                            >
                                <Text fontWeight="bold">{eventLocation.displayLineFirst}</Text>
                            </HighlightBox>
                        )}
                        <DataItem dataKey="Event #">{agencyEventNumber}</DataItem>
                        <DataItem dataKey={renLabel ?? 'REN'}>
                            <Text>{firstRen ?? '-'}</Text>
                        </DataItem>
                        {firstRen && (
                            <IconButton
                                icon="Duplicate"
                                variant="ghost"
                                style={{ marginLeft: 5 }}
                                onClick={() =>
                                    copyText({
                                        text: firstRen,
                                        copiedFieldLabel: renLabel,
                                    })
                                }
                            />
                        )}
                        <FrEventUnitPills units={pillUnits} />
                    </DataList>
                )
            }
        >
            {isEventLoading ? (
                <Spinner size="md" />
            ) : (
                <>
                    <EntityStatusIcon
                        isActive={isActive}
                        isScheduledEvent={isScheduledEvent}
                        size="md"
                    />
                    {shouldShowHeaderActions && (
                        <>
                            {eventLabelsEnabled && (
                                <EventLabelsView
                                    labels={form.getValues('eventLabels')}
                                    truncateCount={1}
                                />
                            )}
                            {showSingleEditButton && (
                                <Button
                                    leadingVisual="Edit"
                                    size="xl"
                                    style={{
                                        position: 'absolute',
                                        right: cssVar('arc.space.4'),
                                        top: cssVar('arc.space.4'),
                                    }}
                                    onClick={goToEditEventPage}
                                >
                                    Edit
                                </Button>
                            )}
                            {showEditDispositionMenu && (
                                <Menu>
                                    <MenuTrigger asChild>
                                        <Button
                                            leadingVisual="Edit"
                                            size="xl"
                                            trailingVisual="Open"
                                            style={{
                                                position: 'absolute',
                                                right: cssVar('arc.space.4'),
                                                top: cssVar('arc.space.4'),
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </MenuTrigger>
                                    <MenuContent>
                                        <MenuItem onSelect={goToEditEventPage}>
                                            Edit Event Details
                                        </MenuItem>
                                        <MenuItem onSelect={goToEditDispositionPage}>
                                            Edit Disposition
                                        </MenuItem>
                                    </MenuContent>
                                </Menu>
                            )}
                        </>
                    )}
                </>
            )}
        </FrPageHeader>
    );
});
